import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Node, mergeAttributes } from '@tiptap/core';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Underline from '@tiptap/extension-underline';
import Heading from '@tiptap/extension-heading';
import 'prosemirror-view/style/prosemirror.css';
import GifPicker from '../../gifs/GifPicker';

const MentionNode = Node.create({
  name: 'boardMention',
  group: 'inline',
  inline: true,
  atom: true,
  selectable: false,

  addAttributes() {
    return {
      id: { default: '' },
      label: { default: '' },
      mentionType: { default: 'user' },
      name: { default: '' },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'span[data-board-mention]',
        getAttrs: (element) => ({
          id: element.getAttribute('data-mention-id') || '',
          label: element.getAttribute('data-mention-label') || element.textContent?.replace(/^@/, '') || '',
          mentionType: element.getAttribute('data-mention-type') || 'user',
          name: element.getAttribute('data-mention-name') || '',
        }),
      },
    ];
  },

  renderHTML({ node, HTMLAttributes }) {
    const label = node.attrs.label || (node.attrs.mentionType === 'board' ? 'board' : node.attrs.mentionType === 'tag' ? 'tag' : 'member');
    return [
      'span',
      mergeAttributes(HTMLAttributes, {
        class: 'board-mention',
        'data-board-mention': 'true',
        'data-mention-id': node.attrs.id || '',
        'data-mention-label': label,
        'data-mention-type': node.attrs.mentionType || 'user',
        'data-mention-name': node.attrs.name || '',
      }),
      `@${label}`,
    ];
  },

  renderText({ node }) {
    const label = node.attrs.label || (node.attrs.mentionType === 'board' ? 'board' : node.attrs.mentionType === 'tag' ? 'tag' : 'member');
    return `@${label}`;
  },
});

const GiphyNode = Node.create({
  name: 'giphyGif',
  group: 'block',
  atom: true,
  selectable: true,

  addAttributes() {
    return {
      providerId: { default: '' },
      title: { default: 'GIF' },
      url: { default: '' },
      previewUrl: { default: '' },
      rating: { default: 'g' },
      width: { default: 0 },
      height: { default: 0 },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'figure[data-giphy-embed="true"]',
        getAttrs: (element) => {
          const image = element.querySelector('img[data-giphy-image]');
          return {
            providerId: element.getAttribute('data-giphy-id') || image?.getAttribute('data-giphy-id') || '',
            title: element.getAttribute('data-giphy-title') || image?.getAttribute('alt') || 'GIF',
            url: image?.getAttribute('src') || '',
            previewUrl: image?.getAttribute('data-preview-src') || image?.getAttribute('src') || '',
            rating: element.getAttribute('data-giphy-rating') || 'g',
            width: Number(image?.getAttribute('width') || 0) || 0,
            height: Number(image?.getAttribute('height') || 0) || 0,
          };
        },
      },
    ];
  },

  renderHTML({ node, HTMLAttributes }) {
    return [
      'figure',
      mergeAttributes(HTMLAttributes, {
        class: 'board-giphy-embed',
        'data-giphy-embed': 'true',
        'data-giphy-id': node.attrs.providerId || '',
        'data-giphy-title': node.attrs.title || 'GIF',
        'data-giphy-rating': node.attrs.rating || 'g',
      }),
      [
        'img',
        {
          'data-giphy-image': 'true',
          'data-giphy-id': node.attrs.providerId || '',
          src: node.attrs.url || node.attrs.previewUrl || '',
          'data-preview-src': node.attrs.previewUrl || node.attrs.url || '',
          alt: node.attrs.title || 'GIF',
          width: node.attrs.width || null,
          height: node.attrs.height || null,
        },
      ],
    ];
  },

  renderText({ node }) {
    return node.attrs.title && node.attrs.title !== 'GIF' ? `[GIF: ${node.attrs.title}]` : '[GIF]';
  },
});

function getMentionTrigger(editor) {
  if (!editor) return null;
  const { state } = editor;
  const { selection } = state;
  if (!selection.empty) return null;

  const { $from, from } = selection;
  const textBefore = $from.parent.textBetween(0, $from.parentOffset, '\n', '\n');
  const match = /(^|[\s([{])@([A-Za-z0-9 ._'’-]{0,48})$/.exec(textBefore);
  if (!match) return null;

  const query = match[2] || '';
  return {
    query,
    range: {
      from: from - query.length - 1,
      to: from,
    },
  };
}

function extractMentionMetadata(editor) {
  const mentionsByUid = new Map();
  const mentionTagsByName = new Map();
  let mentionsBoard = false;

  editor?.state?.doc?.descendants((node) => {
    if (node.type.name !== 'boardMention') return;
    const mentionType = node.attrs.mentionType || 'user';
    if (mentionType === 'board') {
      mentionsBoard = true;
      return;
    }
    if (mentionType === 'tag') {
      const name = String(node.attrs.name || node.attrs.id || node.attrs.label || '').trim();
      if (!name) return;
      mentionTagsByName.set(name.toLowerCase(), {
        name,
        label: String(node.attrs.label || name).trim() || name,
      });
      return;
    }
    const uid = String(node.attrs.id || '').trim();
    if (!uid) return;
    mentionsByUid.set(uid, {
      uid,
      label: String(node.attrs.label || 'Member').trim() || 'Member',
    });
  });

  return {
    mentions: Array.from(mentionsByUid.values()),
    mentionsBoard,
    mentionTags: Array.from(mentionTagsByName.values()),
  };
}

function ToolbarButton({ active = false, onClick, title, children }) {
  return (
    <button
      type="button"
      title={title}
      aria-pressed={active}
      onMouseDown={(event) => {
        event.preventDefault();
      }}
      onClick={onClick}
      className={`btn btn--ghost ${active ? 'btn--active' : ''}`}
      style={{ padding: '0.35rem 0.6rem' }}
    >
      {children}
    </button>
  );
}

export default function RichTextEditor({
  value,
  onChange,
  onMentionMetadataChange,
  searchMentionTargets,
  gifsEnabled = false,
  gifRating = 'g',
  placeholder = 'Write something...',
  minHeight = 180,
}) {
  const [mentionMenu, setMentionMenu] = useState({
    open: false,
    query: '',
    range: null,
    items: [],
    loading: false,
    selectedIndex: 0,
    error: '',
  });
  const searchRequestRef = useRef(0);
  const onMentionMetadataChangeRef = useRef(onMentionMetadataChange);
  const [, setToolbarRevision] = useState(0);
  const [gifPickerOpen, setGifPickerOpen] = useState(false);

  useEffect(() => {
    onMentionMetadataChangeRef.current = onMentionMetadataChange;
  }, [onMentionMetadataChange]);

  const emitMentionMetadata = useCallback((nextEditor) => {
    onMentionMetadataChangeRef.current?.(extractMentionMetadata(nextEditor));
  }, []);

  const refreshMentionMenu = useCallback((nextEditor) => {
    const trigger = getMentionTrigger(nextEditor);
    if (!trigger || !searchMentionTargets) {
      setMentionMenu((current) => (current.open ? { ...current, open: false } : current));
      return;
    }

    setMentionMenu((current) => ({
      ...current,
      open: true,
      query: trigger.query,
      range: trigger.range,
      loading: true,
      error: '',
      items: current.query === trigger.query ? current.items : [],
      selectedIndex: 0,
    }));
  }, [searchMentionTargets]);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: false,
        link: false,
        underline: false,
      }),
      Heading.configure({ levels: [2, 3] }),
      Underline,
      MentionNode,
      GiphyNode,
      Link.configure({
        openOnClick: false,
        autolink: true,
        HTMLAttributes: { rel: 'noopener noreferrer' },
      }),
    ],
    content: value || '',
    editorProps: {
      attributes: {
        class: 'board-rich-editor__content',
        'data-placeholder': placeholder,
      },
    },
    onUpdate({ editor: nextEditor }) {
      onChange?.(nextEditor.getHTML());
      emitMentionMetadata(nextEditor);
      window.setTimeout(() => refreshMentionMenu(nextEditor), 0);
    },
    onSelectionUpdate() {
      setToolbarRevision((revision) => revision + 1);
    },
    onTransaction() {
      setToolbarRevision((revision) => revision + 1);
    },
  });

  const openMentionPicker = useCallback(() => {
    if (!editor || !searchMentionTargets) return;
    const { from } = editor.state.selection;
    editor
      .chain()
      .focus()
      .insertContent('@')
      .run();
    setMentionMenu((current) => ({
      ...current,
      open: true,
      query: '',
      range: { from, to: from + 1 },
      loading: true,
      error: '',
      items: [],
      selectedIndex: 0,
    }));
  }, [editor, searchMentionTargets]);

  const insertGif = useCallback((gif) => {
    if (!editor || !gif?.providerId) return;
    editor
      .chain()
      .focus()
      .insertContent({
        type: 'giphyGif',
        attrs: {
          providerId: gif.providerId,
          title: gif.title || 'GIF',
          url: gif.url || gif.previewUrl || '',
          previewUrl: gif.previewUrl || gif.url || '',
          rating: gif.rating || gifRating || 'g',
          width: gif.width || 0,
          height: gif.height || 0,
        },
      })
      .run();
    setGifPickerOpen(false);
  }, [editor, gifRating]);

  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    if ((value || '') !== current) {
      editor.commands.setContent(value || '', false);
    }
    emitMentionMetadata(editor);
  }, [editor, emitMentionMetadata, value]);

  useEffect(() => {
    if (!mentionMenu.open || !searchMentionTargets) return undefined;
    const requestId = searchRequestRef.current + 1;
    searchRequestRef.current = requestId;

    const timer = window.setTimeout(async () => {
      try {
        const items = await searchMentionTargets({ query: mentionMenu.query });
        if (searchRequestRef.current !== requestId) return;
        setMentionMenu((current) => ({
          ...current,
          items: Array.isArray(items) ? items : [],
          loading: false,
          error: '',
          selectedIndex: 0,
        }));
      } catch (err) {
        console.error('Board mention search failed', err);
        if (searchRequestRef.current !== requestId) return;
        setMentionMenu((current) => ({
          ...current,
          items: [],
          loading: false,
          error: err?.message || 'Could not load mentions.',
          selectedIndex: 0,
        }));
      }
    }, 160);

    return () => window.clearTimeout(timer);
  }, [mentionMenu.open, mentionMenu.query, searchMentionTargets]);

  const insertMention = useCallback((item) => {
    if (!editor || !mentionMenu.range || !item) return;
    const mentionType = item.type === 'board' ? 'board' : item.type === 'tag' ? 'tag' : 'user';
    const label = item.label || (mentionType === 'board' ? 'board' : mentionType === 'tag' ? item.name || 'Tag' : 'Member');
    editor
      .chain()
      .focus()
      .deleteRange(mentionMenu.range)
      .insertContent([
        {
          type: 'boardMention',
          attrs: {
            id: mentionType === 'board' ? 'board' : mentionType === 'tag' ? item.name : item.uid,
            label,
            mentionType,
            name: mentionType === 'tag' ? item.name || label : '',
          },
        },
        { type: 'text', text: ' ' },
      ])
      .run();
    setMentionMenu((current) => ({ ...current, open: false }));
    emitMentionMetadata(editor);
  }, [editor, emitMentionMetadata, mentionMenu.range]);

  const handleMentionKeyDown = useCallback((event) => {
    if (!mentionMenu.open) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      setMentionMenu((current) => ({ ...current, open: false }));
      return;
    }
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setMentionMenu((current) => ({
        ...current,
        selectedIndex: Math.min(current.selectedIndex + 1, Math.max(current.items.length - 1, 0)),
      }));
      return;
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setMentionMenu((current) => ({
        ...current,
        selectedIndex: Math.max(current.selectedIndex - 1, 0),
      }));
      return;
    }
    if (event.key === 'Enter' || event.key === 'Tab') {
      const item = mentionMenu.items[mentionMenu.selectedIndex];
      if (!item) return;
      event.preventDefault();
      insertMention(item);
    }
  }, [insertMention, mentionMenu]);

  if (!editor) {
    return <div className="board-rich-editor">Loading editor…</div>;
  }

  return (
    <div className="board-rich-editor">
      <div className="board-rich-editor__toolbar">
        <ToolbarButton active={editor.isActive('heading', { level: 2 })} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} title="Heading">
          H2
        </ToolbarButton>
        <ToolbarButton active={editor.isActive('bold')} onClick={() => editor.chain().focus().toggleBold().run()} title="Bold">
          B
        </ToolbarButton>
        <ToolbarButton active={editor.isActive('italic')} onClick={() => editor.chain().focus().toggleItalic().run()} title="Italic">
          I
        </ToolbarButton>
        <ToolbarButton active={editor.isActive('underline')} onClick={() => editor.chain().focus().toggleUnderline().run()} title="Underline">
          U
        </ToolbarButton>
        <ToolbarButton active={editor.isActive('bulletList')} onClick={() => editor.chain().focus().toggleBulletList().run()} title="Bullets">
          •
        </ToolbarButton>
        <ToolbarButton active={editor.isActive('orderedList')} onClick={() => editor.chain().focus().toggleOrderedList().run()} title="Numbers">
          1.
        </ToolbarButton>
        <ToolbarButton active={editor.isActive('blockquote')} onClick={() => editor.chain().focus().toggleBlockquote().run()} title="Quote">
          ❝
        </ToolbarButton>
        <ToolbarButton active={mentionMenu.open} onClick={openMentionPicker} title="Mention member, board, or tag">
          @
        </ToolbarButton>
        {gifsEnabled && (
          <ToolbarButton active={gifPickerOpen} onClick={() => setGifPickerOpen((current) => !current)} title="Insert GIF">
            GIF
          </ToolbarButton>
        )}
        <ToolbarButton
          active={editor.isActive('link')}
          onClick={() => {
            const href = window.prompt('Enter URL');
            if (href === null) return;
            if (!href.trim()) {
              editor.chain().focus().unsetLink().run();
              return;
            }
            editor.chain().focus().extendMarkRange('link').setLink({ href: href.trim() }).run();
          }}
          title="Link"
        >
          🔗
        </ToolbarButton>
      </div>

      {gifsEnabled && (
        <GifPicker
          open={gifPickerOpen}
          rating={gifRating}
          surface="boards"
          onSelect={insertGif}
          onClose={() => setGifPickerOpen(false)}
        />
      )}

      <div
        className="board-rich-editor__surface"
        style={{ minHeight }}
        onKeyDownCapture={handleMentionKeyDown}
        onKeyUp={() => refreshMentionMenu(editor)}
        onClick={() => refreshMentionMenu(editor)}
        onMouseDown={(event) => {
          if (event.target === event.currentTarget) {
            event.preventDefault();
            editor.chain().focus().run();
          }
        }}
      >
        <EditorContent editor={editor} />
      </div>

      {mentionMenu.open && (
        <div className="board-mention-menu" role="listbox" aria-label="Mention suggestions">
          {mentionMenu.loading ? (
            <div className="board-mention-menu__empty">Finding mentions...</div>
          ) : mentionMenu.error ? (
            <div className="board-mention-menu__empty">{mentionMenu.error}</div>
          ) : mentionMenu.items.length === 0 ? (
            <div className="board-mention-menu__empty">No matching mentions</div>
          ) : (
            mentionMenu.items.map((item, index) => (
              <button
                type="button"
                key={item.type === 'board' ? 'board' : item.type === 'tag' ? `tag:${item.name}` : item.uid}
                className={`board-mention-menu__item ${index === mentionMenu.selectedIndex ? 'is-active' : ''}`}
                onMouseDown={(event) => {
                  event.preventDefault();
                  insertMention(item);
                }}
              >
                <span className="board-mention-menu__label">@{item.label}</span>
                <span className="board-mention-menu__meta">
                  {item.subtitle && <span className="board-mention-menu__subtitle">{item.subtitle}</span>}
                  {item.type === 'tag' && <span className="board-mention-menu__badge">Tag</span>}
                  {item.isTeamAdmin && <span className="board-mention-menu__badge">Club admin</span>}
                </span>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
